/**
 * Frontend Analytics Utility
 * Tracks page views and custom events
 */

// Generate a unique session ID for this browser session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// API endpoint - uses relative path for production
const API_BASE = '/api/analytics';

/**
 * Track a page view
 * @param {string} path - The page path (e.g., '/arbeiten', '/kontakt')
 */
export const trackPageView = async (path) => {
  try {
    const response = await fetch(`${API_BASE}/pageview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        path: path || window.location.pathname,
        sessionId: getSessionId(),
        language: navigator.language || 'unknown',
        referrer: document.referrer || 'direct',
      }),
    });
    
    if (!response.ok) {
      console.warn('Analytics: Failed to track page view');
    }
  } catch (error) {
    // Silently fail - don't break the site if analytics fails
    console.warn('Analytics: Error tracking page view', error);
  }
};

/**
 * Track a custom event (click, interaction, etc.)
 * @param {string} eventName - The name of the event (e.g., 'click_artwork', 'open_lightbox')
 * @param {object} eventData - Optional additional data about the event
 */
export const trackEvent = async (eventName, eventData = {}) => {
  try {
    const response = await fetch(`${API_BASE}/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventName,
        eventData: {
          ...eventData,
          sessionId: getSessionId(),
          timestamp: new Date().toISOString(),
        },
      }),
    });
    
    if (!response.ok) {
      console.warn('Analytics: Failed to track event');
    }
  } catch (error) {
    // Silently fail
    console.warn('Analytics: Error tracking event', error);
  }
};

// Predefined event trackers for common actions
export const trackArtworkClick = (artworkName) => {
  trackEvent('click_artwork', { artwork: artworkName });
};

export const trackLightboxOpen = (artworkName) => {
  trackEvent('open_lightbox', { artwork: artworkName });
};

export const trackContactFormSubmit = () => {
  trackEvent('submit_contact_form');
};

export const trackSocialClick = (platform) => {
  trackEvent('click_social', { platform });
};

export const trackNavigation = (section) => {
  trackEvent('navigate', { section });
};

export const trackLanguageChange = (language) => {
  trackEvent('change_language', { language });
};

export default {
  trackPageView,
  trackEvent,
  trackArtworkClick,
  trackLightboxOpen,
  trackContactFormSubmit,
  trackSocialClick,
  trackNavigation,
  trackLanguageChange,
};
